import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award,
  Building2,
  RefreshCw,
  FileText,
  ChevronRight,
  Clock,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Lock,
  ArrowUpCircle,
  Info,
  RotateCcw,
} from "lucide-react";
import {
  useGetUpgradeEligibilityQuery,
  useGetMyGradeUpgradesQuery,
} from "../api/upgradeApi";
import { noUpgradeReason } from "../components/UpgradePaymentStep";
import { ceremonyFor, toGradeOf } from "../utils/ceremony";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function extractArray(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  return [];
}

function StatusBadge({ status, paid }: { status?: string; paid?: boolean }) {
  const s = (status ?? "pending_payment").toLowerCase();
  const map: Record<string, { cls: string; icon: React.ReactNode; label: string }> = {
    approved: {
      cls: "bg-green-100 text-green-700",
      icon: <CheckCircle className="w-3 h-3" />,
      label: "Approved",
    },
    rejected: {
      cls: "bg-red-100 text-red-700",
      icon: <XCircle className="w-3 h-3" />,
      label: "Rejected",
    },
    returned: {
      cls: "bg-orange-100 text-orange-700",
      icon: <RotateCcw className="w-3 h-3" />,
      label: "Returned for editing",
    },
    under_review: {
      cls: "bg-blue-100 text-blue-700",
      icon: <AlertCircle className="w-3 h-3" />,
      label: "Under review",
    },
    awaiting_endorsements: {
      cls: "bg-violet-100 text-violet-700",
      icon: <AlertCircle className="w-3 h-3" />,
      label: "Awaiting endorsements",
    },
    recommended: {
      cls: "bg-emerald-100 text-emerald-700",
      icon: <CheckCircle className="w-3 h-3" />,
      label: "Recommended — fees due",
    },
    not_recommended: {
      cls: "bg-orange-100 text-orange-700",
      icon: <AlertCircle className="w-3 h-3" />,
      label: "Not recommended",
    },
    submitted: {
      cls: "bg-indigo-100 text-indigo-700",
      icon: <FileText className="w-3 h-3" />,
      label: "Submitted",
    },
    pending_payment: {
      cls: "bg-amber-100 text-amber-700",
      icon: <Clock className="w-3 h-3" />,
      label: "Awaiting payment",
    },
    draft: {
      cls: "bg-gray-100 text-gray-600",
      icon: <Clock className="w-3 h-3" />,
      // A reopened application is a paid draft being corrected — don't call it "not paid".
      label: paid ? "Being corrected" : "Draft — not paid",
    },
    pending: {
      cls: "bg-amber-100 text-amber-700",
      icon: <Clock className="w-3 h-3" />,
      label: "Awaiting payment",
    },
  };
  // An unmapped status shows ITSELF, neutrally. Falling back to `draft` was
  // worse than saying nothing: it told a member whose application had reached
  // the committee that it was an unpaid draft — every status added since this
  // map was written (awaiting_endorsements, recommended, not_recommended) read
  // that way. Unknown still must not read as "Submitted" either.
  const { cls, icon, label } = map[s] ?? {
    cls: "bg-gray-100 text-gray-600",
    icon: <FileText className="w-3 h-3" />,
    label: s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}
    >
      {icon}
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Recommended — induction / investiture fees outstanding             */
/* ------------------------------------------------------------------ */
/**
 * Being recommended is the point at which the ball returns to the member, and
 * nothing on this page previously said so — the status word changed and the
 * applicant was left to work out that money was now due. This spells out what
 * is owed, what paying achieves, and where to do it.
 */
function RecommendedFeesBanner({ apps }: { apps: any[] }) {
  const due = (apps ?? []).filter(
    (a) => String(a.status).toLowerCase() === "recommended"
  );

  if (due.length === 0) return null;

  /*
   * The ceremony this banner can name.
   *
   * A Member is inducted and a Fellow invested, so the heading can only use one
   * of those words when every upgrade on it ends at the same ceremony. Somebody
   * holding both a Member and a Fellow recommendation gets the neutral wording
   * rather than one of the two applied to both — the per-application buttons
   * below name each one correctly.
   */
  const ceremonies = Array.from(
    new Set(due.map((a: any) => ceremonyFor(toGradeOf(a)).noun)),
  );
  const ceremony = ceremonies.length === 1 ? ceremonies[0] : null;
  const feesWord = ceremony ? `${ceremony} fees` : "fees";

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 md:p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
          <AlertCircle className="w-5 h-5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-amber-900 first-letter:uppercase">
            {due.length === 1
              ? `Your upgrade has been recommended — ${feesWord} are due`
              : `${due.length} of your upgrades have been recommended — ${feesWord} are due`}
          </h3>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            The Membership Committee has recommended your application. To
            complete your upgrade you now need to settle the fees set for it —
            charged together as{" "}
            <span className="font-semibold">one payment</span>. Your upgrade is
            approved
            {ceremony ? ` and your place at the ${ceremony} booked` : ""} as soon
            as the payment clears, so nothing further is required from you
            afterwards. You can also buy extra access cards for non-member guests
            at the same time.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {due.map((a: any) => (
              <Link
                key={`${a._kind}-${a.id}`}
                to={a._href}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition-colors"
              >
                {due.length === 1
                  ? "View breakdown & pay"
                  : `Pay for ${a._label}`}
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Eligibility banner                                                 */
/* ------------------------------------------------------------------ */
function UpgradeBanner({
  elig,
  loading,
}: {
  elig: any;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="rounded-2xl border bg-white p-6 flex items-center gap-3 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Checking your upgrade eligibility…</span>
      </div>
    );
  }
  if (!elig) return null;

  // Already at the top of the ladder (e.g. Fellow).
  if (!elig.target_grade) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 flex items-start gap-4 shadow-sm">
        <div className="w-12 h-12 rounded-xl bg-amber-100 grid place-items-center shrink-0">
          <Lock className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-amber-900">No upgrade available</h2>
          <p className="text-sm text-amber-800 mt-0.5">{noUpgradeReason(elig)}</p>
          {elig.current_grade && (
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-white border border-amber-200 text-amber-700 text-xs font-semibold">
              <Award className="w-3.5 h-3.5" /> Current grade:{" "}
              {elig.current_grade.name}
            </span>
          )}
        </div>
      </div>
    );
  }

  // An upgrade is already being processed.
  if (elig.active_upgrade) {
    return (
      <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-6 flex items-start gap-4 shadow-sm">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 grid place-items-center shrink-0">
          <Info className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-indigo-900">
            Upgrade in progress ({elig.current_grade?.name} →{" "}
            {elig.target_grade?.name})
          </h2>
          <p className="text-sm text-indigo-800 mt-0.5">
            Your upgrade application has been submitted and is being processed.
            Track its status in the list below.
          </p>
        </div>
      </div>
    );
  }

  // Eligible — show the CTA + route preview.
  const routeFees = (elig.routes ?? [])
    .map((r: any) => r.fee)
    .filter((f: any) => f != null);
  const feeHint = elig.requires_route
    ? routeFees.length
      ? `from ${naira(Math.min(...routeFees))}`
      : null
    : elig.fee != null
    ? naira(elig.fee)
    : null;

  return (
    <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-600 via-indigo-600 to-blue-700 text-white p-6 shadow-lg">
      <div className="absolute -top-10 -right-10 w-44 h-44 bg-white/10 rounded-full blur-2xl" />
      <div className="relative flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
            <ArrowUpCircle className="w-3.5 h-3.5" /> You’re eligible to upgrade
          </div>
          <h2 className="text-2xl font-bold">
            {elig.current_grade?.name} → {elig.target_grade?.name}
          </h2>
          <p className="text-sm text-indigo-100 mt-1">
            Update your details and pay the upgrade fee
            {feeHint ? ` (${feeHint})` : ""} to submit your application.
          </p>

          {/* CPD + route preview chips */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="px-2.5 py-1 rounded-lg bg-white/10 text-xs font-medium">
              Your CPD: {elig.cpd?.total ?? 0}
            </span>
            {elig.requires_route &&
              (elig.routes ?? []).map((r: any) => (
                <span
                  key={r.route}
                  className="px-2.5 py-1 rounded-lg bg-white/10 text-xs font-medium capitalize"
                >
                  {r.route} route: {naira(r.fee)}
                  {r.cpd_required != null && ` · ${r.cpd_required} CPD`}
                </span>
              ))}
          </div>
        </div>
        <Link
          to="/upgrade-applications/grade"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white text-indigo-700 text-sm font-semibold hover:bg-indigo-50 transition-colors shadow"
        >
          Start upgrade <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Outstanding requirements preview */}
      {Array.isArray(elig.payment_blockers) &&
        elig.payment_blockers.length > 0 && (
          <div className="relative mt-4 pt-4 border-t border-white/15">
            <p className="text-xs font-semibold text-indigo-100 mb-1.5">
              Before you can pay, you still need to:
            </p>
            <ul className="space-y-1">
              {elig.payment_blockers.map((b: any, i: number) => (
                <li
                  key={`${b.code}-${i}`}
                  className="flex items-start gap-2 text-xs text-indigo-50"
                >
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-200" />
                  <span>{b.message}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

/* ================================================================== */
/* PAGE                                                                */
/* ================================================================== */
export default function MyUpgradeApplicationsPage() {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: elig,
    isLoading: eligLoading,
    isFetching: eligFetching,
    refetch: refetchElig,
  } = useGetUpgradeEligibilityQuery();

  const {
    data: gradeRaw,
    isLoading: gradeLoading,
    isFetching: gradeFetching,
    error: gradeError,
    refetch: refetchGrade,
  } = useGetMyGradeUpgradesQuery();

  const refetch = () => {
    refetchElig();
    refetchGrade();
  };

  const apps = useMemo(() => {
    const grade = extractArray(gradeRaw).map((a) => {
      // Not paid yet (still draft / awaiting payment) -> resume the wizard;
      // otherwise -> the full read-only details page.
      const unpaid = ["draft", "pending_payment"].includes(
        String(a.status).toLowerCase()
      );
      return {
        ...a,
        _kind: "grade",
        _label: `${a.from_category?.name ?? "Upgrade"} → ${
          a.to_category?.name ?? ""
        }`,
        _route: a.route,
        _href: unpaid
          ? "/upgrade-applications/grade"
          : `/upgrade-applications/grade/${a.id}`,
      };
    });
    return [...grade].sort(
      (a, b) =>
        new Date(b.created_at ?? 0).getTime() -
        new Date(a.created_at ?? 0).getTime()
    );
  }, [gradeRaw]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return apps.filter((app) => {
      const blob = `${app.id ?? ""} ${app._label ?? ""} ${
        app._route ?? ""
      }`.toLowerCase();
      const matchQ = !nq || blob.includes(nq);
      const matchS =
        statusFilter === "all" ||
        (app.status ?? "").toLowerCase() === statusFilter;
      return matchQ && matchS;
    });
  }, [apps, q, statusFilter]);

  const stats = useMemo(
    () => ({
      total: apps.length,
      pending: apps.filter((a) =>
        ["pending_payment", "pending", "submitted"].includes(
          (a.status ?? "").toLowerCase()
        )
      ).length,
      // A committee verdict is still "with the committee" from the member's
      // point of view — neither is a finished application, and counting only
      // 'under_review' would drop them out of the tile the moment a member
      // decided.
      under_review: apps.filter((a) =>
        ["under_review", "recommended", "not_recommended"].includes(
          (a.status ?? "").toLowerCase()
        )
      ).length,
      approved: apps.filter(
        (a) => (a.status ?? "").toLowerCase() === "approved"
      ).length,
    }),
    [apps]
  );

  const listLoading = gradeLoading;
  const isFetching = eligFetching || gradeFetching;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
            <Award className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="font-semibold text-gray-900">My Upgrade Applications</h1>
            <p className="text-sm text-gray-500">
              Upgrade your membership grade and track your applications.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refetch}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <Link
            to="/firm-applications"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
          >
            <Building2 className="w-4 h-4" />
            Firm registration
          </Link>
          {/* Always-visible entry point — eligibility is confirmed inside the
              flow (and at the payment step), not here. */}
          <Link
            to="/upgrade-applications/grade"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-semibold transition-colors shadow-sm"
          >
            <ArrowUpCircle className="w-4 h-4" />
            Start upgrade
          </Link>
        </div>
      </div>

      {/* Eligibility banner */}
      <UpgradeBanner elig={elig} loading={eligLoading} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats.total} color="text-gray-700" />
        <StatCard
          label="Pending / Submitted"
          value={stats.pending}
          color="text-amber-600"
        />
        <StatCard
          label="Under Review"
          value={stats.under_review}
          color="text-blue-600"
        />
        <StatCard
          label="Approved"
          value={stats.approved}
          color="text-green-600"
        />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-7 relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by ID, grade, route…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>
        <div className="md:col-span-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
          >
            <option value="all">All Statuses</option>
            <option value="pending_payment">Awaiting payment</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="recommended">Recommended — fees due</option>
            <option value="not_recommended">Not recommended</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <button
            onClick={() => {
              setQ("");
              setStatusFilter("all");
            }}
            className="w-full px-3 py-2 border rounded-lg text-sm hover:bg-gray-50 text-gray-500 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Recommended → fees outstanding. Sits above the list and ignores the
          filters, so the one thing the member has to act on is not hidden by a
          status filter they left set. */}
      <RecommendedFeesBanner apps={apps} />

      {/* List */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        {listLoading ? (
          <div className="py-20 flex flex-col items-center gap-2 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">Loading applications…</p>
          </div>
        ) : gradeError ? (
          <div className="py-20 text-center space-y-3">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
            <p className="text-sm text-red-500">
              Could not load applications. Please try again.
            </p>
            <button
              onClick={refetch}
              className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center space-y-3">
            <FileText className="w-10 h-10 mx-auto text-gray-200" />
            <p className="text-sm text-gray-400">
              {apps.length === 0
                ? "You haven’t started any upgrade applications yet."
                : "No applications match your filters."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((app: any) => {
              const isFirm = app._kind === "firm";
              // The committee has recommended this one and the fees are the only
              // thing left. The member has no way of knowing that from a status
              // word alone, so the row says it outright.
              const needsInduction =
                String(app.status).toLowerCase() === "recommended";
              const row = (
                <div className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
                        isFirm
                          ? "bg-green-50 border-green-100"
                          : "bg-indigo-50 border-indigo-100"
                      }`}
                    >
                      {isFirm ? (
                        <Building2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowUpCircle className="w-5 h-5 text-indigo-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {app._label}
                        </p>
                        {app._route && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-semibold uppercase">
                            {app._route} route
                          </span>
                        )}
                        <StatusBadge status={app.status} paid={!!app.payment_id} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmtDate(app.created_at)}
                        </span>
                        {app.amount != null && (
                          <span className="text-gray-400">
                            {naira(Number(app.amount) / 100)}
                          </span>
                        )}
                      </div>

                      {needsInduction && (
                        <p className="mt-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 max-w-xl">
                          <span className="font-bold">Action required:</span>{" "}
                          The Membership Committee has recommended your upgrade.
                          Pay the fees set for this upgrade to complete it — your
                          upgrade is approved and your place at the{" "}
                          {ceremonyFor(toGradeOf(app)).eventNoun} booked as soon
                          as the payment clears. Open this application to see the
                          breakdown and pay.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    <span className="hidden lg:block text-[10px] font-mono text-gray-300 mr-2">
                      #{String(app.id).padStart(6, "0")}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                        needsInduction
                          ? "bg-gray-900 border-gray-900 text-white"
                          : "border-gray-200 text-gray-600"
                      }`}
                    >
                      {/* "Pay induction fees" on a Member upgrade, "Pay
                          investiture fees" on a fellowship one — a Fellow is
                          invested, not inducted. See utils/ceremony. */}
                      {needsInduction
                        ? ceremonyFor(toGradeOf(app)).payLabel
                        : "View Details"}
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              );

              return (
                <Link
                  key={`${app._kind}-${app.id}`}
                  to={app._href}
                  className="block"
                >
                  {row}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
